import React, {createContext, useMemo} from 'react';

import {
  PlanType,
  useRegistryOrganizationQuery,
  useRegistryProjectMembersQuery,
} from '../../../generated/graphql';
import {Viewer} from '../../../state/viewer/types';
import {isCommunityEditionPrimarySubscription} from '../../../util/accounts/pricing';
import {propagateErrorsContext} from '../../../util/errors';
import {AccessOption} from '../../../util/permissions';
import {useRampFlagRegistryObserverRole} from '../../../util/rampFeatureFlags';
import {checkIsServiceAccount} from '../../../util/serviceAccounts';
import {OrganizationMemberRole} from '../../MembersTable/util';
import {globalRegistryType} from '../common/utils';
import {
  OrganizationRow,
  QueryOrganizationTeam,
  QueryRegistryOrganizationMember,
  QueryRegistryOrganizationServiceAccount,
  RegistryMember,
  RegistryMemberType,
  RegistryRoles,
  RegistryTeamMember,
} from './RegistryMembersTable/types';
import {
  augmentRegistryMembers,
  getIsViewerRegistryAdmin,
  getRegistryMembers,
  getRegistryTeamMembers,
} from './RegistryMembersTable/utils';

const HiddenServerUserEmails = [
  'local@wandb.com',
  'restore@wandb.com',
  'service@wandb.com',
];

export type RegistryMembersContextType = {
  registryMembers: RegistryMember[];
  registryTeamMembers: RegistryTeamMember[];
  organizationMember: OrganizationRow;
  organizationMembersFiltered: QueryRegistryOrganizationMember[];
  isRegistryMembersQueryLoading: boolean;
  isOrganizationQueryLoading: boolean;
  isViewerRegistryAdmin: boolean;
  organizationTeams: Omit<QueryOrganizationTeam, 'members'>[];
  serviceAccounts: QueryRegistryOrganizationServiceAccount[];
};

// Registries homepage search context
export const RegistryMembersContext = createContext<RegistryMembersContextType>(
  {
    registryMembers: [],
    registryTeamMembers: [],
    organizationMember: {
      role: RegistryRoles.Viewer,
      name: '',
      numUsers: 0,
      memberType: RegistryMemberType.Organization,
    },
    organizationMembersFiltered: [],
    isRegistryMembersQueryLoading: true,
    isOrganizationQueryLoading: true,
    isViewerRegistryAdmin: false,
    organizationTeams: [],
    serviceAccounts: [],
  }
);

export const RegistryMembersContextProvider: React.FC<{
  registry: globalRegistryType;
  viewer?: Viewer;
}> = ({registry, viewer, children}) => {
  const {data: organizationData, loading: isOrganizationQueryLoading} =
    useRegistryOrganizationQuery({
      variables: {orgName: registry.orgName},
      context: propagateErrorsContext(),
    });
  const {data: registryMembersData, loading: isRegistryMembersQueryLoading} =
    useRegistryProjectMembersQuery({
      variables: {
        entityName: registry.entityName,
        projectName: registry.registryProjectName,
      },
      context: propagateErrorsContext(),
      fetchPolicy: 'network-only',
    });
  const {data: enableRestrictedViewerRole} = useRampFlagRegistryObserverRole(
    registry.orgName
  );

  const {organizationMember, organizationMembersFiltered} = useMemo(() => {
    // We need the subscription information for the organization row
    // If the org is on a Community plan, then the org row role is `Admin`
    // Otherwise, `Viewer`
    const primarySub = organizationData?.organization?.subscriptions?.find(
      s => s.plan.planType === PlanType.Primary
    );
    const isPrimarySubCommunityEdition =
      primarySub && isCommunityEditionPrimarySubscription(primarySub);

    // The OrganizationMembers query doesn't filter out service accounts
    // for on-prem install so the client needs to do it
    const rawMembers = !isOrganizationQueryLoading
      ? (organizationData?.organization?.members ?? [])
      : [];
    const organizationMembersFiltered =
      rawMembers.filter(
        member =>
          !checkIsServiceAccount(member.user.accountType) &&
          !HiddenServerUserEmails.includes(member.email) &&
          member.role !== OrganizationMemberRole.BILLING_ONLY
      ) ?? [];

    const defaultOrganizationRole = isPrimarySubCommunityEdition
      ? RegistryRoles.Admin
      : enableRestrictedViewerRole
        ? RegistryRoles.RestrictedViewer
        : RegistryRoles.Viewer;

    const organizationMember: OrganizationRow = {
      role: defaultOrganizationRole,
      name: registry.orgName,
      numUsers: organizationMembersFiltered.length ?? 0,
      memberType: RegistryMemberType.Organization,
    };

    return {organizationMember, organizationMembersFiltered};
  }, [
    enableRestrictedViewerRole,
    isOrganizationQueryLoading,
    organizationData?.organization?.members,
    organizationData?.organization?.subscriptions,
    registry.orgName,
  ]);

  const {registryMembers, registryTeamMembers} = useMemo(() => {
    const registryTeamMembers = getRegistryTeamMembers(
      registryMembersData?.project?.teamMembers ?? []
    );
    const registryMembers = getRegistryMembers(
      organizationData?.organization?.members ?? [],
      registryMembersData?.project?.members ?? []
    );
    const augmentedRegistryMembers = augmentRegistryMembers(
      registryMembers,
      registryTeamMembers,
      registry.access === AccessOption.Private ? organizationMember : undefined,
      enableRestrictedViewerRole
    );

    return {registryMembers: augmentedRegistryMembers, registryTeamMembers};
  }, [
    registryMembersData?.project?.teamMembers,
    registryMembersData?.project?.members,
    organizationData?.organization?.members,
    registry.access,
    organizationMember,
    enableRestrictedViewerRole,
  ]);

  const isViewerRegistryAdmin = useMemo(() => {
    const registryMembersMap = new Map<string, RegistryMember>();
    registryMembers.forEach(member =>
      registryMembersMap.set(member.id, member)
    );
    const registryTeamMembersMap = new Map<string, RegistryTeamMember>();
    registryTeamMembers.forEach(member =>
      registryTeamMembersMap.set(member.id, member)
    );
    return getIsViewerRegistryAdmin(
      registryMembersMap,
      registryTeamMembersMap,
      viewer
    );
  }, [registryMembers, registryTeamMembers, viewer]);

  return (
    <RegistryMembersContext.Provider
      value={{
        registryMembers,
        registryTeamMembers,
        organizationMember,
        organizationMembersFiltered,
        isRegistryMembersQueryLoading,
        isOrganizationQueryLoading,
        isViewerRegistryAdmin,
        organizationTeams: organizationData?.organization?.teams ?? [],
        serviceAccounts: organizationData?.organization?.serviceAccounts ?? [],
      }}>
      {children}
    </RegistryMembersContext.Provider>
  );
};
